import React from 'react';
import { IAutomatedCreditControlResultsQueryResult } from '../../graphql/schema/p4types';
import { OGL_COLOURS } from '../../styles/themes/default/widgetColours';
import { ResponsiveBar } from '@nivo/bar';

export const defaultBackgroundColour = (_: any, index: number) => OGL_COLOURS[index % OGL_COLOURS.length];
export const defaultLabelTextColour = (_: any, index: number): `#${string}` => '#FFFFFF';

export type IWorkspaceOverviewBarProps = {
  //Should be compatible with all workspaces, but specifying the type as a particular one helps us with code completion/etc
  listData: IAutomatedCreditControlResultsQueryResult['listData'];
  backgroundColour?: (groupColumnKey: string, index: number) => `#${string}`;
  labelTextColour?: (groupColumnKey: string, index: number) => `#${string}`;
  defs?: Array<any>; //Define patterns (or gradients, but currently not using that in P4) for graph elements.
  fill?: Array<any>; //Define match configurations for using patterns.
};

export const useWorkspaceOverviewDataMap = (listData: IAutomatedCreditControlResultsQueryResult['listData']) => {
  if (!listData?.aggregateRows) return;

  const aggregateDataBySumColumn: { [sumColumn: string]: { [groupColumn: string]: {} } } = {};
  listData?.aggregateRows?.forEach((aggregateRow) => {
    aggregateRow?.aggregateColumns?.forEach((aggregateColumn) => {
      const columnName = aggregateColumn?.columnName;
      const groupColumnName = aggregateRow?.groupColumns?.[0]?.value;
      if (!columnName || !groupColumnName) return;
      if (!aggregateDataBySumColumn[columnName]) {
        aggregateDataBySumColumn[columnName] = {};
      }
      aggregateDataBySumColumn[columnName][groupColumnName] = aggregateColumn?.sum;
    });
  });
  return aggregateDataBySumColumn;
};

/**
 * Displays a bar chart above a Workspace if the aggregate criteria is set accordingly (I.e. If it has groupColumns being sent up).
 * Note - WorkspaceOverviewWaffle can also be used as a drop-in alternative to this component.
 */
const WorkspaceOverviewBar = ({
  listData,
  backgroundColour = defaultBackgroundColour,
  labelTextColour = defaultLabelTextColour,
  defs,
  fill,
}: IWorkspaceOverviewBarProps) => {
  //Construct an object containing the data in an easier map format.
  const aggregateDataBySumColumn = useWorkspaceOverviewDataMap(listData);
  if (!aggregateDataBySumColumn || !listData?.aggregateRows) return null;

  //Convert our map into @nivo data format for compatability with the bar graph.
  const fullDataMap: Array<{ [key: string]: any }> = [];
  Object.keys(aggregateDataBySumColumn).forEach((sumColumnKey) => {
    const aggregateData = aggregateDataBySumColumn[sumColumnKey];
    const dataMap: { [key: string]: any } = { totalKey: sumColumnKey };
    Object.keys(aggregateData).forEach((groupColumnKey, idx) => {
      dataMap[groupColumnKey] = aggregateData[groupColumnKey];
      dataMap[`${groupColumnKey}Colour`] = backgroundColour(groupColumnKey, idx);
    });
    fullDataMap.push(dataMap);
  });

  const keys = listData.aggregateRows.map((aggregateRow, idx) => {
    return aggregateRow?.groupColumns?.[0]?.value!;
  });

  return (
    <div style={{ height: '150px' }}>
      <ResponsiveBar
        data={fullDataMap}
        keys={keys}
        indexBy={'totalKey'}
        margin={{}} //left: 120 (If left legend enabled)
        padding={0.3}
        layout="horizontal"
        colors={({ data, id }) => data[`${id}Colour`]}
        defs={defs || []}
        fill={fill || []}
        borderColor={{
          from: 'color',
          modifiers: [['darker', 1.6]],
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={null}
        axisLeft={null}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={(d) => {
          return labelTextColour(`${d.data.id}`, d.index);
        }}
        legends={[]}
        role="application"
        ariaLabel="Credit Control Overview"
        barAriaLabel={(e) => `${e.id}: ${e.formattedValue}`}
        valueFormat={(value) => {
          return value.toFixed(2);
        }}
      />
    </div>
  );
};

export default WorkspaceOverviewBar;
